import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Color, ErrorRecord, Product, ProductCategory } from "../../types";
import { SoecoImageList } from "./SoecoImageList";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import Cropper, { Area, Point } from "react-easy-crop";
import React from "react";
import { getCroppedImg, readFile } from "../../utils/util";
import { SoecoButton } from "../../components/SoecoButton";
import Div100vh from "react-div-100vh";
import { useGetPredictions } from "../../hooks/productHooks";
import Card from "@mui/material/Card";
import { Loader } from "../../components/Loader";
import { useSnackBar } from "../../context/SnackbarContext";
import { imagesUrl } from "../../services/endpoints";
import { normalizeColors } from "../../utils/util";
import { CardActions, Typography } from "@mui/material";

type Props = {
    images: File[];
    companyId: string;
    inventoryId: string;
    product: Product;
    setProduct: (product: Product) => void;
    setOptions: (product: ProductOptions) => ProductOptions;
    getManufacturerLabel: (idOrLabel: string) => string;
    getDesignerLabel: (idOrLabel: string) => string;
    defaultPicture: boolean;
    colors: Color[];
};

export interface ProductOptions {
    manufacturer: string | undefined;
    categories: string[] | undefined;
    designer: string | undefined;
}

export const PredictProduct = (props: Props) => {
    if (props.defaultPicture) {
        return (
            <PredictionWrapper>
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: "30px",
                        width: "90%",
                        overflowY: "auto",
                        maxHeight: "80vh",
                        padding: "5px",
                        marginBottom: "30px",
                    }}
                >
                    <StyledCardActions>
                        <IconActionsWrapper>
                            <Typography
                                fontFamily={"'Poppins', sans-serif"}
                                fontSize={18}
                                color="#8c8c8c"
                                fontWeight={600}
                            >
                                No image
                            </Typography>
                        </IconActionsWrapper>
                    </StyledCardActions>
                    <Typography
                        fontFamily={'"Yllw Sans a1 241219", sans-serif'}
                        fontSize={26}
                        color="black"
                        fontWeight={700}
                    >
                        Using the default picture.
                    </Typography>
                    <Typography
                        fontFamily={"'Yllw Sans a1 241219', sans-serif"}
                        fontSize={26}
                        color="black"
                        fontWeight={700}
                    >
                        Don't forget to add one later.
                    </Typography>
                </Card>
            </PredictionWrapper>
        );
    }

    const {
        data: predictions,
        isLoading,
        isError,
    } = useGetPredictions(props.companyId, props.inventoryId, props.product, props.images);
    const snackBar = useSnackBar();

    if (predictions && props.product.predictions?.length === 0) {
        const predictionStrings: string[] = [];

        for (const pred of predictions) {
            predictionStrings.push(pred.id || "");
        }

        props.setProduct({
            ...props.product,
            predictions: predictionStrings,
        });
    }
    const selectPrediction = (prediction: Product) => {
        const colors = normalizeColors(prediction.color ? prediction.color : "", props.colors);
        const options: ProductOptions = {
            manufacturer: prediction.manufacturer,
            designer: prediction.designer,
            categories: prediction.categories,
        };

        const newOptions: ProductOptions = props.setOptions(options);

        const predicts: string[] = [];

        if (predictions) {
            for (const prediction of predictions) {
                predicts.push(prediction.id ?? "");
            }
        }

        const newProduct: Product = {
            ...prediction,
            id: props.product.id,
            inventoryId: props.inventoryId,
            companyId: props.companyId,
            imageUrls: props.product.imageUrls,
            quantity: 1,
            manufacturer: newOptions.manufacturer,
            designer: newOptions.designer,
            categories: newOptions.categories,
            predictions: predicts,
            predictionUsed: prediction.id,
            colors: colors,
        };

        props.setProduct(newProduct);
        snackBar.showSnackBar(
            `Product data loaded ${prediction.name ? `from ${prediction.name}` : ""}!`,
            "success",
        );
    };

    if (isLoading) {
        return (
            <PredictionWrapper>
                <h1>Fetching product predictions...</h1>
                <Loader />
            </PredictionWrapper>
        );
    }

    if (isError) {
        return (
            <PredictionWrapper>
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: "30px",
                        width: "90%",
                        overflowY: "auto",
                        maxHeight: "80vh",
                        padding: "5px",
                        marginBottom: "30px",
                    }}
                >
                    <StyledCardActions>
                        <IconActionsWrapper>
                            <Typography
                                fontFamily={"'Poppins', sans-serif"}
                                fontSize={18}
                                color="#8c8c8c"
                                fontWeight={600}
                            >
                                Error
                            </Typography>
                        </IconActionsWrapper>
                    </StyledCardActions>
                    <h1>An error occured when getting predictions</h1>
                    <h2>Is your device offline?</h2>
                </Card>
            </PredictionWrapper>
        );
    }

    return (
        <>
            <PredictionWrapper>
                <Typography
                    fontFamily={"'Poppins', sans-serif"}
                    fontSize={18}
                    color="#8c8c8c"
                    fontWeight={500}
                >
                    AI Looking for similar product
                </Typography>
                <PredictionsContainer>
                    {predictions?.map(prediction => {
                        return (
                            <CardWrapper
                                key={prediction.id}
                                onClick={() => selectPrediction(prediction)}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: "30px",
                                        width: "90%",
                                        overflowY: "auto",
                                        maxHeight: "80vh",
                                        padding: "5px",
                                        marginBottom: "30px",
                                    }}
                                >
                                    <StyledCardActions>
                                        <IconActionsWrapper>
                                            <Typography
                                                fontFamily={"'Poppins', sans-serif"}
                                                fontSize={18}
                                                color="#8c8c8c"
                                                fontWeight={600}
                                            >
                                                {prediction.name}
                                            </Typography>
                                        </IconActionsWrapper>
                                    </StyledCardActions>
                                    {prediction.manufacturer && (
                                        <Typography
                                            fontFamily={"'Yllw Sans a1 241219', sans-serif"}
                                            fontSize={14}
                                            color="black"
                                            fontWeight={500}
                                        >
                                            {props.getManufacturerLabel(prediction.manufacturer)}
                                        </Typography>
                                    )}
                                    {prediction.designer && (
                                        <Typography
                                            fontFamily={"'Yllw Sans a1 241219', sans-serif"}
                                            fontSize={14}
                                            color="black"
                                            fontWeight={500}
                                        >
                                            {props.getDesignerLabel(prediction.designer)}
                                        </Typography>
                                    )}
                                    {prediction.imageUrls &&
                                        (prediction.imageUrls[0].includes("img.soeco.se") ? (
                                            <img
                                                src={prediction.imageUrls[0]}
                                                alt=""
                                                width="180px"
                                                height="auto"
                                            />
                                        ) : (
                                            <img
                                                src={`${imagesUrl}${prediction.imageUrls[0]}`}
                                                alt=""
                                                width="180px"
                                                height="auto"
                                            />
                                        ))}
                                </Card>
                                {/* <Card>
                                    {prediction.imageUrls &&
                                        (prediction.imageUrls[0].includes("img.soeco.se") ? (
                                            <img
                                                src={prediction.imageUrls[0]}
                                                alt=""
                                                width="180px"
                                                height="auto"
                                            />
                                        ) : (
                                            <img
                                                src={`${imagesUrl}${prediction.imageUrls[0]}`}
                                                alt=""
                                                width="180px"
                                                height="auto"
                                            />
                                        ))}

                                    <h2>{prediction.name}</h2>
                                    {prediction.manufacturer && (
                                        <p>{props.getManufacturerLabel(prediction.manufacturer)}</p>
                                    )}
                                    {prediction.designer && (
                                        <p>{props.getDesignerLabel(prediction.designer)}</p>
                                    )}
                                </Card> */}
                            </CardWrapper>
                        );
                    })}
                </PredictionsContainer>
            </PredictionWrapper>
        </>
    );
};

const CardWrapper = styled.button`
    background-color: transparent;
    border-color: transparent;
`;

const PredictionWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const PredictionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: center;
    padding-top: 30px;
`;
const IconActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    gap: 5px;
`;
const StyledCardActions = styled(CardActions)`
    display: flex;
    width: 100%;
    font-size: 3rem;
    border-bottom: 1px solid #ebebeb;
    //background-color: #ebebeb;
    align-items: center; /* Centrerar vertikalt i flex-container */
`;
const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
`;
